import React, { useState, useEffect, useCallback } from "react"
import styled from "styled-components"
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton"
import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { MdArrowBack, MdChevronRight, MdPerson, MdAccessTime, MdFileDownload } from "react-icons/md"

// Reads the Clara audit trail (the clara_audit table in db_iam) straight from the Clara
// service, grouped by chat session. Two views: a list of sessions, and the reconstructed
// conversation for one session.

// The full base URL of Clara's API, prefix included, same convention as ClaraChat:
// REACT_APP_CLARA_API_URL from env-config.js in deployments (path routed through the
// dashboard's own host). Without it Clara is not configured.
const CLARA_API = window.env?.REACT_APP_CLARA_API_URL

function fmtTime(iso) {
	if (!iso) return "—"
	try {
		return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
	} catch {
		return iso
	}
}

function fmtMs(ms) {
	if (ms == null) return "—"
	return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}

export function ClaraAuditTab() {
	const { getToken } = useAuthProvider()
	const { selectedWorkspace } = useWorkspacesProvider()

	const [sessions, setSessions] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [openSession, setOpenSession] = useState(null) // conversation_id
	const [turns, setTurns] = useState([])
	const [turnsLoading, setTurnsLoading] = useState(false)
	const [exporting, setExporting] = useState(false)

	const fetchSessions = useCallback(async () => {
		try {
			if (!CLARA_API) throw new Error("Clara is not configured.")
			const response = await fetch(`${CLARA_API}/admin/sessions`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace }),
			})
			if (!response.ok) {
				throw new Error(await response.text())
			}
			const data = await response.json()
			setSessions(Array.isArray(data) ? data : [])
			setError(null)
		} catch (e) {
			setError(typeof e === "string" ? e : e.message)
		} finally {
			setLoading(false)
		}
	}, [getToken, selectedWorkspace])

	useEffect(() => {
		fetchSessions()
	}, [fetchSessions])

	// Download every chat in the workspace as one CSV. Clara builds the file: the answers
	// contain commas, quotes and newlines, and Python's csv module escapes them correctly.
	// A plain <a href> can't be used because the route is token gated, so fetch the bytes
	// and hand the browser a blob.
	const exportCsv = useCallback(async () => {
		setExporting(true)
		try {
			const response = await fetch(`${CLARA_API}/admin/export`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace }),
			})
			if (!response.ok) {
				throw new Error(await response.text())
			}
			const blob = await response.blob()
			const url = URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.download = `clara-chats-${selectedWorkspace || "all"}-${new Date().toISOString().slice(0, 10)}.csv`
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(url)
		} catch (e) {
			setError(typeof e === "string" ? e : e.message)
		} finally {
			setExporting(false)
		}
	}, [getToken, selectedWorkspace])

	const openConversation = useCallback(async (conversationId) => {
		setOpenSession(conversationId)
		setTurns([])
		setTurnsLoading(true)
		try {
			const response = await fetch(`${CLARA_API}/admin/get_session`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ conversation_id: conversationId, workspace_name: selectedWorkspace }),
			})
			if (!response.ok) {
				throw new Error(await response.text())
			}
			const data = await response.json()
			setTurns(Array.isArray(data) ? data : [])
		} catch (e) {
			setError(typeof e === "string" ? e : e.message)
		} finally {
			setTurnsLoading(false)
		}
	}, [getToken, selectedWorkspace])

	if (loading) {
		return <Center><MobergLogoSpinner size={64} /></Center>
	}

	if (error) {
		return <ErrorBanner>{error}</ErrorBanner>
	}

	// ---- Conversation detail view ----
	if (openSession) {
		return (
			<Wrap>
				<DetailHeader>
					<MobergButton
						onClick={() => { setOpenSession(null); setTurns([]) }}
						variant={MobergButtonVariant.OUTLINE}
						theme={MobergTheme.BLUE}
						shape={MobergButtonShape.RECTANGLE}
					>
						<MdArrowBack size={16} /> All sessions
					</MobergButton>
					<DetailMeta>Session <Mono>{openSession}</Mono></DetailMeta>
				</DetailHeader>

				{turnsLoading ? (
					<Center><MobergLogoSpinner size={48} /></Center>
				) : (
					<Conversation>
						{turns.map(turn => (
							<Turn key={turn.id}>
								<UserBubble>{turn.question}</UserBubble>
								<ClaraBubble $status={turn.status}>
									{turn.answer || <Muted>({turn.status}{turn.error ? `: ${turn.error}` : ""})</Muted>}
									<TurnMeta>
										<StatusPill $status={turn.status}>{turn.status}</StatusPill>
										<MetaItem>total {fmtMs(turn.latency_ms)}</MetaItem>
										<MetaDim>parse {fmtMs(turn.parse_ms)} · compute {fmtMs(turn.compute_ms)} · narrate {fmtMs(turn.narrate_ms)}</MetaDim>
										{turn.source === "voice" && <MetaItem>🎤 voice</MetaItem>}
										<MetaDim>{fmtTime(turn.created_at)}</MetaDim>
									</TurnMeta>
								</ClaraBubble>
							</Turn>
						))}
						{turns.length === 0 && <Empty>No turns in this session.</Empty>}
					</Conversation>
				)}
			</Wrap>
		)
	}

	// ---- Session list view ----
	return (
		<Wrap>
			<Toolbar>
				<ToolbarCount>{sessions.length} {sessions.length === 1 ? "session" : "sessions"}</ToolbarCount>
				<MobergButton
					onClick={exportCsv}
					variant={MobergButtonVariant.OUTLINE}
					theme={MobergTheme.BLUE}
					shape={MobergButtonShape.RECTANGLE}
					disabled={exporting || sessions.length === 0}
				>
					<MdFileDownload size={16} /> {exporting ? "Exporting…" : "Export all chats (CSV)"}
				</MobergButton>
			</Toolbar>
			<ListHeader>
				<HCell style={{ flex: 2 }}>Session</HCell>
				<HCell style={{ width: 90, textAlign: "center" }}>Turns</HCell>
				<HCell style={{ width: 120 }}>Patient</HCell>
				<HCell style={{ width: 90 }}>User</HCell>
				<HCell style={{ width: 130 }}>Total time</HCell>
				<HCell style={{ width: 160 }}>Last activity</HCell>
				<HCell style={{ width: 28 }} />
			</ListHeader>
			<ListBody>
				{sessions.map(s => (
					<SessionRow key={s.conversation_id} onClick={() => openConversation(s.conversation_id)}>
						<Cell style={{ flex: 2 }}><Mono>{s.conversation_id}</Mono></Cell>
						<Cell style={{ width: 90, justifyContent: "center" }}><CountPill>{s.turns}</CountPill></Cell>
						<Cell style={{ width: 120 }}>{s.patient_id != null ? `#${s.patient_id}` : <Muted>—</Muted>}</Cell>
						<Cell style={{ width: 90 }}><MdPerson size={13} /> {s.user_id ?? "—"}</Cell>
						<Cell style={{ width: 130 }}>{fmtMs(s.total_latency_ms)}</Cell>
						<Cell style={{ width: 160 }}><MdAccessTime size={13} /> {fmtTime(s.last_at)}</Cell>
						<Cell style={{ width: 28, justifyContent: "center" }}><MdChevronRight size={18} color="var(--text-secondary-color)" /></Cell>
					</SessionRow>
				))}
				{sessions.length === 0 && <Empty>No Clara chat sessions recorded yet.</Empty>}
			</ListBody>
		</Wrap>
	)
}

const Wrap = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
`

const Center = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
`

const ErrorBanner = styled.div`
	padding: 10px 16px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--color-danger-text);
	background: var(--color-danger-hover-bg);
	border-radius: 6px;
	border: 1px solid var(--color-danger-text);
`

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 4px 8px 12px;
`

const ToolbarCount = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-secondary-color);
`

const ListHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 8px;
	border-bottom: 1px solid var(--color-border-default);
`

const HCell = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: var(--text-secondary-color);
`

const ListBody = styled.div`
	flex: 1;
	overflow-y: auto;
`

const SessionRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 14px 8px;
	cursor: pointer;
	transition: background-color 0.15s;

	&:hover { background-color: var(--color-hover-neutral); }
	& + & { border-top: 1px solid var(--color-border-light); }
`

const Cell = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--color-text-primary);
`

const Mono = styled.span`
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 12px;
	color: var(--color-text-primary);
`

const CountPill = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 600;
	padding: 2px 10px;
	border-radius: 12px;
	color: var(--color-accent-interactive);
	background: var(--color-hover-blue);
`

const Muted = styled.span`
	color: var(--text-secondary-color);
`

const Empty = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--text-secondary-color);
	text-align: center;
	padding: 48px 20px;
`

const DetailHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	padding-bottom: 14px;
	margin-bottom: 8px;
	border-bottom: 1px solid var(--color-border-default);
`

const DetailMeta = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-secondary-color);
`

const Conversation = styled.div`
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 18px;
	padding: 8px 4px;
`

const Turn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const UserBubble = styled.div`
	align-self: flex-end;
	max-width: 70%;
	padding: 10px 14px;
	border-radius: 14px 14px 4px 14px;
	background: var(--color-accent-interactive);
	color: #fff;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
`

const ClaraBubble = styled.div`
	align-self: flex-start;
	max-width: 80%;
	padding: 12px 16px;
	border-radius: 14px 14px 14px 4px;
	background: var(--color-surface-panel);
	border: 1px solid var(--color-border-default);
	color: var(--color-text-primary);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	white-space: pre-wrap;
`

const TurnMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
	margin-top: 10px;
	padding-top: 8px;
	border-top: 1px solid var(--color-border-light);
	font-size: 11px;
`

const MetaItem = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	color: var(--color-text-primary);
	font-weight: 600;
`

const MetaDim = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	color: var(--text-secondary-color);
`

const StatusPill = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	padding: 2px 8px;
	border-radius: 10px;
	color: ${({ $status }) => $status === "answered" ? "var(--color-success-text)" : $status === "error" ? "var(--color-danger-text)" : "var(--text-secondary-color)"};
	background: ${({ $status }) => $status === "answered" ? "var(--color-success-hover-bg)" : $status === "error" ? "var(--color-danger-hover-bg)" : "var(--color-hover-neutral)"};
`
